import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Star, Zap, Heart, Flame, ShoppingCart, ArrowRight, Instagram, Twitter, Facebook, User, LogOut } from 'lucide-react';
import { mockData } from '../utils/mock';
import NewsletterPopup from '../components/NewsletterPopup';
import FeedbackForm from '../components/FeedbackForm';
import ProductCarousel from '../components/ProductCarousel';
import FestiveBanner from '../components/FestiveBanner';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const { addToCart, getTotalItems, cartItems } = useCart();
  const { customer, logout, isAuthenticated } = useCustomerAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/products`);
      setProducts(data);
    } catch (error) {
      // Fallback to mock data if API fails
      setProducts(mockData.products);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleShopNow = () => {
    navigate('/cart');
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    // Optional: Show toast notification
  };

  const handleGoToCart = () => {
    navigate('/cart');
  };

  const getProductQuantityInCart = (productId) => {
    const item = cartItems.find(item => item.id === productId);
    return item ? item.quantity : 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Newsletter Popup */}
      <NewsletterPopup />

      {/* Festive Banner - Ganesh Chaturthi */}
      <FestiveBanner />

      {/* Header */}
      <header className="fixed top-0 w-full bg-white/80 backdrop-blur-md z-50 border-b border-orange-100">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Flame className="w-8 h-8 text-orange-500" />
            <h1 className="text-2xl font-bold text-gray-900">Roastey Snacks</h1>
          </div>
          <nav className="hidden md:flex gap-6">
            <a href="#products" className="text-gray-700 hover:text-orange-500 transition-colors">Products</a>
            <a href="#benefits" className="text-gray-700 hover:text-orange-500 transition-colors">Why Us</a>
            <a href="#testimonials" className="text-gray-700 hover:text-orange-500 transition-colors">Reviews</a>
          </nav>
          <div className="flex items-center gap-3">
            {/* Customer Auth */}
            {isAuthenticated && customer ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full bg-orange-50 hover:bg-orange-100 transition-colors"
                >
                  {customer.picture ? (
                    <img 
                      src={customer.picture} 
                      alt={customer.name}
                      className="w-7 h-7 rounded-full"
                    />
                  ) : (
                    <User className="w-5 h-5 text-orange-600" />
                  )}
                  <span className="font-medium text-gray-800 text-sm">{customer.name?.split(' ')[0]}</span>
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <button
                      onClick={() => {
                        navigate('/my-orders');
                        setShowUserMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      My Orders
                    </button>
                    <button
                      onClick={() => {
                        logout();
                        setShowUserMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-red-600 text-sm"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="border-orange-500 text-orange-600 hover:bg-orange-50"
              >
                Sign In
              </Button>
            )}
            
            {/* Cart Button */}
            <button
              onClick={handleGoToCart}
              className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ShoppingCart className="w-6 h-6 text-gray-700" />
              {getTotalItems() > 0 && (
                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {getTotalItems()}
                </span>
              )}
            </button>
            <Button onClick={handleShopNow} className="bg-orange-500 hover:bg-orange-600 text-white">
              Shop Now
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-yellow-400 text-gray-900 hover:bg-yellow-500 text-sm px-4 py-1 font-semibold">
                🔥 High Protein. Zero Guilt.
              </Badge>
              <h2 className="text-5xl md:text-7xl font-extrabold text-gray-900 leading-tight" style={{fontFamily: 'Fredoka, sans-serif'}}>
                Snack Smart,
                <span className="text-orange-500"> Not Hard</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Ditch the drama of unhealthy munchies. Our baked & roasted snacks are loaded with protein 
                and flavour. Because adulting is hard enough without boring snacks.
              </p>
              <div className="flex gap-4 flex-wrap">
                <Button onClick={handleShopNow} size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg">
                  Shop The Vibe <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button variant="outline" size="lg" className="border-orange-500 text-orange-500 hover:bg-orange-50 px-8 py-6 text-lg">
                  Learn More
                </Button>
              </div>
              <div className="flex gap-8 pt-4">
                <div>
                  <p className="text-3xl font-bold text-orange-500">High</p>
                  <p className="text-sm text-gray-600">Protein</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-orange-500">100%</p>
                  <p className="text-sm text-gray-600">Baked/Roasted</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-orange-500">Zero</p>
                  <p className="text-sm text-gray-600">Artificial Junk</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-orange-200 to-yellow-200 rounded-3xl blur-2xl opacity-30"></div>
              <div className="relative rounded-3xl shadow-2xl bg-white overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_roastey-snacks/artifacts/fp33gpra_Ragi%20Bhujia%20Landing%20Page.png" 
                  alt="Roastey Snacks - Ragi Bhujia - Healthy Protein Snacks"
                  className="w-full h-auto object-contain max-h-[500px] p-6"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-orange-100 text-orange-600 mb-4 font-semibold">Our Bestsellers</Badge>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Fredoka, sans-serif'}}>
              Snacks That Actually Slap 🔥
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              No cap, these are the real MVPs of healthy snacking
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {loading ? (
              <div className="col-span-3 text-center py-12">
                <p className="text-xl text-gray-600">Loading products...</p>
              </div>
            ) : (
              products.map((product, index) => (
              <Card 
                key={product.id}
                className="overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 hover:border-orange-300 cursor-pointer transform hover:-translate-y-2"
                onMouseEnter={() => setHoveredProduct(product.id)}
                onMouseLeave={() => setHoveredProduct(null)}
              >
                {/* Product Image Carousel */}
                <div className="relative">
                  <ProductCarousel images={product.images} productName={product.name} />
                  {product.badge && (
                    <Badge className="absolute top-4 left-4 bg-red-500 text-white z-10">
                      {product.badge}
                    </Badge>
                  )}
                </div>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <h4 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h4>
                    <p className="text-gray-600">{product.description}</p>
                  </div>
                  
                  {/* Nutritional Information Capsules */}
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 font-semibold">Nutritional Info (per 100g):</p>
                    <div className="flex gap-2 flex-wrap">
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-full px-3 py-1.5 flex items-center gap-1.5">
                        <span className="text-green-700 font-bold text-sm">💪 Protein:</span>
                        <span className="text-green-900 font-semibold text-sm">{product.nutrition.protein}</span>
                      </div>
                      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-full px-3 py-1.5 flex items-center gap-1.5">
                        <span className="text-blue-700 font-bold text-sm">🍯 Sugar:</span>
                        <span className="text-blue-900 font-semibold text-sm">{product.nutrition.sugar}</span>
                      </div>
                      <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-full px-3 py-1.5 flex items-center gap-1.5">
                        <span className="text-yellow-700 font-bold text-sm">🥑 Fat:</span>
                        <span className="text-yellow-900 font-semibold text-sm">{product.nutrition.fat}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`star-${product.id}-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant="secondary" className="bg-orange-50 text-orange-600">
                      High Protein
                    </Badge>
                    <Badge variant="secondary" className="bg-green-50 text-green-600">
                      {product.type}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-3">
                      {product.originalPrice && (
                        <span className="text-xl text-gray-400 line-through font-medium">
                          ₹{product.originalPrice}
                        </span>
                      )}
                      <span className="text-3xl font-bold text-orange-500">₹{product.price}</span>
                      {product.originalPrice && (
                        <span className="text-sm font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">
                          {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    <Button 
                      onClick={() => navigate(`/product/${product.id}`)}
                      variant="outline"
                      className="flex-1 border-orange-500 text-orange-600 hover:bg-orange-50"
                    >
                      View Details
                    </Button>
                    <Button 
                      onClick={() => handleAddToCart(product)}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white relative"
                    >
                      <ShoppingCart className="mr-2 w-4 h-4" />
                      {getProductQuantityInCart(product.id) > 0 ? (
                        <>
                          Added ({getProductQuantityInCart(product.id)})
                        </>
                      ) : (
                        'Add to Cart'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )))}
          </div>

          {/* View All Products Button */}
          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/products')}
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg"
            >
              View All Products ({products.length}) →
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 px-4 bg-gradient-to-b from-orange-50 to-yellow-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-yellow-400 text-gray-900 mb-4 font-semibold">The Roastey Difference</Badge>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Fredoka, sans-serif'}}>
              Why We're Built Different 💪
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Not your regular snack brand. We're here to make healthy snacking cool again.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockData.benefits.map((benefit) => (
              <div 
                key={benefit.title}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-400 rounded-2xl flex items-center justify-center mb-6 transform hover:rotate-12 transition-transform">
                  {benefit.icon === 'zap' && <Zap className="w-8 h-8 text-white" />}
                  {benefit.icon === 'heart' && <Heart className="w-8 h-8 text-white" />}
                  {benefit.icon === 'flame' && <Flame className="w-8 h-8 text-white" />}
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3">{benefit.title}</h4>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-white rounded-3xl p-12 shadow-xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h4 className="text-3xl font-bold text-gray-900 mb-6" style={{fontFamily: 'Fredoka, sans-serif'}}>
                  From Our Kitchen to Your Cravings 🍽️
                </h4>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Every pack is crafted with love (and a lot of roasting). We believe Indians deserve 
                  better snacking options - ones that don't make you feel guilty at 3 AM. So we said 
                  "bye Felicia" to fried junk and hello to protein-packed goodness.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-gray-700">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    100% Natural Ingredients
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    No Preservatives or Artificial Colors
                  </li>
                  <li className="flex items-center gap-3 text-gray-700">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    High Protein, Low Drama
                  </li>
                </ul>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://images.unsplash.com/photo-1633536705550-c06044327482" 
                  alt="Mixed Nuts"
                  className="rounded-2xl shadow-lg object-cover h-48"
                />
                <img 
                  src="https://images.unsplash.com/photo-1614807618309-9de716b16eac" 
                  alt="Roasted Almonds"
                  className="rounded-2xl shadow-lg object-cover h-48 mt-8"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-orange-100 text-orange-600 mb-4 font-semibold">Real Talk</Badge>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Fredoka, sans-serif'}}>
              Don't Take Our Word For It 👀
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Here's what the snack squad is saying
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockData.testimonials.map((testimonial) => (
              <Card key={testimonial.name} className="border-2 hover:border-orange-300 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`star-${testimonial.name}-${i}`} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic leading-relaxed">"{testimonial.review}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-400 rounded-full flex items-center justify-center text-white font-bold">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Feedback Form Section */}
      <FeedbackForm />

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-orange-500 to-red-500">
        <div className="container mx-auto text-center">
          <h3 className="text-4xl md:text-6xl font-bold text-white mb-6" style={{fontFamily: 'Fredoka, sans-serif'}}>
            Ready to Level Up Your Snack Game? 🚀
          </h3>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of Indians making the switch to healthier snacking. Your taste buds will thank you.
          </p>
          <Button 
            onClick={handleShopNow}
            size="lg" 
            className="bg-white text-orange-500 hover:bg-gray-100 px-12 py-6 text-xl font-bold"
          >
            Shop Now <ShoppingCart className="ml-2 w-6 h-6" />
          </Button>
          <p className="text-white/80 mt-6">Free shipping on orders above ₹499 🎉</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Flame className="w-6 h-6 text-orange-500" />
                <h4 className="text-xl font-bold">Roastey Snacks</h4>
              </div>
              <p className="text-gray-400">Making healthy snacking cool, one roast at a time.</p>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Quick Links</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#products" className="hover:text-orange-500 transition-colors">Products</a></li>
                <li><a href="#benefits" className="hover:text-orange-500 transition-colors">Why Us</a></li>
                <li><a href="#testimonials" className="hover:text-orange-500 transition-colors">Reviews</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Support</h5>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-orange-500 transition-colors">FAQs</a></li>
                <li><a href="#" className="hover:text-orange-500 transition-colors">Shipping</a></li>
                <li><a href="#" className="hover:text-orange-500 transition-colors">Returns</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Follow The Vibe</h5>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-orange-500 transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-orange-500 transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-orange-500 transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Roastey Snacks. All rights reserved. Made with 🧡 in India</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
